<template>
  <div :class="$style.vipWrapper">
    <div v-if="!isPlayerAuthorised && data.banner">
      <MainBannerSlider is-without-animation :banners="pageBanners" @buttonClick="openSignup" />
    </div>
    <div v-else-if="currentLevelData">
      <h2 :class="$style.pageTitle">{{ data.title }}</h2>
      <div :class="$style.currentLevel">
        <div :class="$style.currentLevelContainer">
          <!-- <img :class="$style.purple" src="/images/infopage/vip/purple_small_circle.png" />
          <img :class="$style.blue" src="/images/infopage/vip/blue_small_circle.png" /> -->
          <img :class="$style.currentLevelImage" :src="currentLevelData.levelImage.url" />
        </div>
        <div :class="$style.currentLevelContent">
          <VipProgress
            :points-title="data.pointsTitle"
            :to-next-level-title="data.toNextLevelTitle"
            :max-level-title="data.maxLevelTitle"
          />
          <div :class="$style.currentLevelRewards">
            <div :class="$style.currentLevelRewardsTitle">
              <span>{{ data.levelRewardsTitle }}</span>
            </div>
            <div
              :class="[
                $style.currentLevelRewardsList,
                {
                  [$style.currentLevelRewardsListWithSingleItem]:
                    currentLevelActiveRewards && currentLevelActiveRewards.length === 1,
                },
              ]"
            >
              <VipRewardCard
                v-for="(reward, index) in currentLevelActiveRewards"
                :key="index"
                :reward="reward"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
    <InfoPageCashbackFever
      v-if="data.cashbackVipShowBlock"
      :title="data.cashbackVipTitle"
      :end-date="data.cashbackVipDataEndDate"
      :start-date="data.cashbackVipDataStartDate"
      :text-markdown="data.cashbackVipText"
      :button-title="isPlayerAuthorised ? storeButtons.deposit : storeButtons.signup"
      :url-banner="data.cashbackVipBanner.url"
      :url-banner-tablet="data.cashbackVipBannerTabler.url"
      :card-data="data.cashbackCard"
      :terms="data.cashbackVipTerms"
    />
    <div :class="{ [$style.flexReverse]: isPlayerAuthorised }">
      <div :class="[$style.vipPreferences, { [$style.preferencesAuthorized]: isPlayerAuthorised }]">
        <h2>{{ data.preferencesTitle }}</h2>
        <div :class="$style.vipPreferencesCard">
          <VipPreferenceCard
            v-for="(preference, index) in data.preferences"
            :key="index"
            :preference="preference"
          />
        </div>
        <div :class="$style.vipPreferencesDescription">
          <Markdown :text="data.preferencesDescription" />
        </div>
        <BitButton
          v-if="!isPlayerAuthorised && !isDesktop"
          :class="[$style.preferencesRegisterButton, { [$style.alignSelfCenter]: isMobile }]"
          @click="openSignup"
        >
          {{ data.registerButtonTitle }}
        </BitButton>
      </div>
      <div
        :class="[
          $style.levelBlock,
          {
            [$style.levelBlockAuthorised]: isPlayerAuthorised,
            [$style.levelBlockAuthorisedNoMarginTop]:
              isPlayerAuthorised && isMobile && isShowMoreButton,
          },
        ]"
      >
        <h2 v-if="levelsToShow.length" :class="$style.loyaltyTitle">
          {{ data.loyaltyProgramTitle }}
        </h2>
        <div :class="[$style.levels, { [$style.levelAuthorized]: isPlayerAuthorised }]">
          <VipLevelCard
            v-for="level in levelsToShow"
            :key="level.id"
            :level="level"
            :status="getLevelStatus(level.level)"
          />
        </div>
        <BitButton
          v-if="isDesktop && !isPlayerAuthorised"
          :class="[$style.vipRegisterButton, $style.marginBottomCustom]"
          @click="openSignup"
        >
          {{ data.registerButtonTitle }}
        </BitButton>
        <BitButton
          v-if="!isDesktop && isShowMoreButton"
          :class="[
            $style.showVipLevelButton,
            { [$style.marginBottom]: !isDesktop && !isPlayerAuthorised },
          ]"
          type="secondary"
          @click="isShowMoreButton = false"
        >
          <template #prependIcon>
            <img src="/images/common/expand_more.svg?url_encode" alt="" />
          </template>
          {{ showMoreButton }}
        </BitButton>
        <BitButton
          v-if="!isDesktop && !isShowMoreButton"
          :class="[
            $style.showVipLevelButton,
            { [$style.marginBottom]: !isDesktop && !isPlayerAuthorised },
          ]"
          type="secondary"
          @click="closeExpandedLevels"
        >
          <template #prependIcon>
            <img src="/images/common/expand_less.svg?url_encode" alt="" />
          </template>
          {{ showLessButton }}
        </BitButton>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { BitButton } from '@bit/ui-kit';
import { computed, onMounted, ref, nextTick, useCssModule } from 'vue';
import InfoPageCashbackFever from '~/components/infoPage/cashbackFever.vue';
import { MainBannerSlider } from '~/components/lib/mainPage';
import { VipLevelCard, VipRewardCard, VipProgress, VipPreferenceCard } from '~/components/lib/vip';
import Markdown from '~/components/markdown.vue';
import { useStoreModals } from '~/stores/abstract/modals';
import { useStoreSsPlayer } from '~/stores/softswiss/player';
import { useStoreButtonTitles } from '~/stores/strapi/buttonTitles';
import { useStoreVipLevelData } from '~/stores/strapi/vipLevelData';
import { EVipLevelStatus } from '~/types/vip';
import type { ComponentInfoPageBlocksVipBlock } from '~/types/infoPages';
import type { SliderBannerData } from '~/types/slider';

const props = defineProps({
  data: {
    type: Object as PropType<ComponentInfoPageBlocksVipBlock>,
    required: true,
  },
});

const storeSsPlayer = useStoreSsPlayer();
const storeVipLevelData = useStoreVipLevelData();
const storeModals = useStoreModals();
const storeButtons = useStoreButtonTitles();
const $style = useCssModule();
const isClientReady = ref(false);

const isPlayerAuthorised = ref(false);

const isShowMoreButton = ref(true);

const currentLevelActiveRewards = computed(() =>
  storeVipLevelData.currentLevelData?.rewards.filter((reward) => reward.isEnabled)
);

const levelsToShow = computed(() => {
  if (!isShowMoreButton.value || usePlatform().isDesktop) {
    return storeVipLevelData.data;
  } else if (usePlatform().isTablet || (usePlatform().isMobile && !isPlayerAuthorised.value)) {
    return storeVipLevelData.data.slice(0, 4);
  } else {
    return [];
  }
});

const pageBanners = computed((): SliderBannerData[] => {
  return [
    {
      buttonText: props.data.registerButtonTitle,
      frontImage: props.data.banner.desktopAssetFront!,
      subtitleFirst: props.data.title,
      subtitleSecond: props.data.subtitle,
    },
  ];
});

const currentLevelData = computed(() => storeVipLevelData.currentLevelData);
const isMobile = computed(() => usePlatform().isMobile);
const isDesktop = computed(() => usePlatform().isDesktop);
const showMoreButton = computed(() => storeButtons.showAllLevels);
const showLessButton = computed(() => storeButtons.showLessGames);
const openSignup = () => storeModals.openLoginOrSignupModal({ button_place: 'vip_page' });

const getLevelStatus = (level: number) => {
  if (!isPlayerAuthorised.value || storeSsPlayer.playerLevel > level) {
    return EVipLevelStatus.passed;
  } else if (storeSsPlayer.playerLevel === level) {
    return EVipLevelStatus.active;
  } else {
    return EVipLevelStatus.blocked;
  }
};

let loadPromise: Promise<any>;

const closeExpandedLevels = async () => {
  isShowMoreButton.value = true;

  const showMoreButton = document.querySelector(`.${$style.showVipLevelButton}`);

  await nextTick();

  showMoreButton?.scrollIntoView({ behavior: 'smooth' });
};

onMounted(async () => {
  isPlayerAuthorised.value = storeSsPlayer.isPlayerAuthorised;
  isClientReady.value = true;
  await loadPromise;
});

watch(
  () => storeSsPlayer.isPlayerAuthorised,
  () => {
    isPlayerAuthorised.value = storeSsPlayer.isPlayerAuthorised;
  }
);
</script>

<style lang="scss" module src="./vipBlock.scss"></style>
